import { useId, useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { buttonClass } from "@/components/buttonStyles";
import { FIELD_ERROR, FIELD_HELP, FIELD_LABEL } from "@/components/fieldStyles";
import { utcTodayIso } from "@/lib/dates";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import {
  bindRefusalOf,
  canSelect,
  canUnselect,
  isOpenBindRequest,
  selectQuote,
  unselectQuote,
  type BindRefusal,
} from "@/lib/submissions/quoteBinding";
import { NOTE_MAX } from "@/lib/submissions/quoteConditions";
import { useSendQuoteNote } from "@/lib/submissions/useSendQuoteNote";

// BRK-S209 — choosing a quote (PMS-S554) from its popup, and BRK-S373 —
// asking Signal to cancel a bind request (PMS-S759). Neither changes anything here until PMS
// answers: the quotes are read again and the card and popup show where it
// stands from that (NN #7).

/** A refusal as one sentence under its button. */
function refusalLine(refusal: BindRefusal): string {
  switch (refusal.kind) {
    case "network":
      return "We couldn’t reach Signal. Check your connection, then try again.";
    case "conflict":
    case "refused":
      return refusal.message;
    case "fields":
      return refusal.summary;
    // A bind's own answers: select and unselect call no carrier.
    case "not_bindable":
    case "key_reused":
    case "carrier_failed":
      return "This quote can’t change from where it stands.";
  }
}

/** A secondary action that asks PMS once, then reads the quotes again. */
function useQuoteAction(onChanged: () => void | Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  async function run(send: () => Promise<unknown>) {
    setBusy(true);
    setProblem(null);
    try {
      await send();
      await onChanged();
    } catch (err) {
      const refusal = bindRefusalOf(err);
      setProblem(refusalLine(refusal));
      // A conflict means the quote moved on: show where it is now.
      if (refusal.kind === "conflict") void onChanged();
    } finally {
      setBusy(false);
    }
  }
  return { busy, problem, run };
}

/** "Select this quote", or "Unselect" once chosen; nothing otherwise. */
export function QuoteSelection({
  submissionId,
  quote,
  onChanged,
}: {
  submissionId: string;
  quote: BrokerQuote;
  onChanged: () => void | Promise<void>;
}) {
  const { authedRequest, identity } = useAuth();
  const { busy, problem, run } = useQuoteAction(onChanged);
  const select = canSelect(quote, identity, utcTodayIso());
  if (!select && !canUnselect(quote, identity)) return null;
  return (
    <div className="flex flex-col gap-1 sm:items-end">
      <button
        type="button"
        aria-busy={busy || undefined}
        onClick={() =>
          void run(() =>
            select
              ? selectQuote(authedRequest, submissionId, quote.id)
              : unselectQuote(authedRequest, submissionId, quote.id),
          )
        }
        className={buttonClass({ variant: "secondary" })}
      >
        {select
          ? busy
            ? "Selecting…"
            : "Select this quote"
          : busy
            ? "Unselecting…"
            : "Unselect"}
      </button>
      {problem && (
        <p role="alert" className="text-meta text-brk-danger">
          {problem}
        </p>
      )}
    </div>
  );
}

/** The note that asks Signal to cancel, as it starts: the broker may edit it. */
export const CANCEL_REQUEST_NOTE =
  "Please cancel the bind request for this quote.";

/**
 * "Ask Signal to cancel", while Signal hasn't decided the request (BRK-S373;
 * Tyler, 2026-10-17). Only Signal withdraws a bind request, taking it up with
 * the carrier (PMS-S759), so the broker asks with a note on the quote, which
 * Signal reads (BRK-S362). It says it's sent once PMS has the note, and the
 * request stands as PMS says until Signal withdraws it. A broker's note
 * doesn't alert Signal yet, so it promises no more than a note.
 */
export function AskSignalToCancel({
  submissionId,
  quote,
  onChanged,
}: {
  submissionId: string;
  quote: BrokerQuote;
  onChanged: () => void | Promise<void>;
}) {
  const fieldId = useId();
  const errorId = useId();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(CANCEL_REQUEST_NOTE);
  const [sent, setSent] = useState(false);
  const { send, saving, problem } = useSendQuoteNote(submissionId, quote.id);

  async function ask(event: FormEvent) {
    event.preventDefault();
    const note = await send(body);
    if (!note) return;
    setSent(true);
    void onChanged();
  }

  if (!isOpenBindRequest(quote)) return null;
  if (sent) {
    return (
      <p role="status" className="text-brk-text">
        Sent to Signal as a note on this quote.
      </p>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
      >
        Ask Signal to cancel
      </button>
    );
  }
  return (
    <form noValidate onSubmit={ask} className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className={FIELD_LABEL}>
        Message to Signal
      </label>
      <textarea
        id={fieldId}
        rows={3}
        value={body}
        maxLength={NOTE_MAX}
        onChange={(e) => setBody(e.target.value)}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? errorId : undefined}
        className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
      />
      <p className={FIELD_HELP}>
        Only Signal can cancel a bind request: it takes it up with the carrier.
        This goes to Signal as a note on this quote.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={saving}
          aria-busy={saving || undefined}
          className={buttonClass({ size: "sm" })}
        >
          {saving ? "Sending…" : "Send to Signal"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={saving}
          className={buttonClass({ variant: "secondary", size: "sm" })}
        >
          Not now
        </button>
      </div>
      {problem && (
        <p id={errorId} role="alert" className={FIELD_ERROR}>
          {problem}
        </p>
      )}
    </form>
  );
}
